import useMarkGrantAsPaid from '@/api/mutations/grants/useMarkGrantAsPaid';
import useMarkGrantAsUnpaid from '@/api/mutations/grants/useMarkGrantAsUnpaid';
import { Button } from '@payaca/ambrosia';
import { GrantsPermissions } from '@payaca/permissions/grants/grants.permissions';
import { FC } from 'react';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';

/**
 * The permission-gated "Mark grant as paid" / "Mark grant as unpaid" control.
 * Owns both mutations so the payment-schedule stage and the standalone grant
 * list render the identical action without duplicating the wiring.
 */
export const GrantPaidControls: FC<{
  grantLineId: string;
  projectPublicId: string;
  isPaid: boolean;
  onSuccess?: () => void;
}> = ({ grantLineId, projectPublicId, isPaid, onSuccess }) => {
  const { mutate: markGrantAsPaid, isPending: isMarkingPaid } =
    useMarkGrantAsPaid({
      projectId: projectPublicId,
      options: { onSuccess: () => onSuccess?.() },
    });
  const { mutate: markGrantAsUnpaid, isPending: isMarkingUnpaid } =
    useMarkGrantAsUnpaid({
      projectId: projectPublicId,
      options: { onSuccess: () => onSuccess?.() },
    });

  return (
    <PermissionGuard
      renderIfHasPermissions={[GrantsPermissions.MARK_GRANT_AS_PAID]}
    >
      {isPaid ? (
        <Button
          size="sm"
          variant="link"
          disabled={isMarkingUnpaid}
          onClick={() => markGrantAsUnpaid({ grantLineId })}
        >
          Mark grant as unpaid
        </Button>
      ) : (
        <Button
          size="sm"
          variant="tertiary"
          disabled={isMarkingPaid}
          onClick={() => markGrantAsPaid({ grantLineId })}
        >
          Mark grant as paid
        </Button>
      )}
    </PermissionGuard>
  );
};
